import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { ServicesWorkOrdersApiService } from './services-work-orders-api.service';
import { WoConnectivityService } from './offline/wo-connectivity.service';

@Injectable({ providedIn: 'root' })
export class ServicesWorkOrderChecklistPhotoService {
  private readonly api = inject(ServicesWorkOrdersApiService);
  private readonly connectivity = inject(WoConnectivityService);

  /**
   * Persist capture to Filesystem. Upload immediately when online; otherwise return local path only.
   */
  async persistChecklistPhoto(
    workOrderId: number,
    checklistOrder: number,
    file: File,
  ): Promise<{ imageUrl: string; localPhotoPath: string }> {
    const contentType = file.type?.trim() || 'image/jpeg';
    const localPhotoPath = await this.writeLocalFile(workOrderId, checklistOrder, file);

    if (!this.connectivity.online()) {
      return { imageUrl: '', localPhotoPath };
    }

    try {
      const imageUrl = await this.uploadFileToBackend(workOrderId, checklistOrder, file, contentType);
      try {
        await Filesystem.deleteFile({ path: localPhotoPath, directory: Directory.Data });
      } catch {
        /* keep local copy if delete fails */
      }
      return { imageUrl, localPhotoPath: '' };
    } catch {
      // Network race: keep local file for outbox later
      return { imageUrl: '', localPhotoPath };
    }
  }

  /** Upload a previously stored local JPEG (outbox flush). */
  async uploadLocalChecklistPhoto(
    workOrderId: number,
    checklistOrder: number,
    localPath: string,
    contentType = 'image/jpeg',
  ): Promise<string> {
    const file = await this.readLocalAsFile(localPath, checklistOrder, contentType);
    return this.uploadFileToBackend(workOrderId, checklistOrder, file, contentType);
  }

  async uploadChecklistPhoto(workOrderId: number, checklistOrder: number, file: File): Promise<string> {
    const contentType = file.type?.trim() || 'image/jpeg';
    return this.uploadFileToBackend(workOrderId, checklistOrder, file, contentType);
  }

  /** Resolve a usable preview URI for a Filesystem path under Directory.Data. */
  async resolveLocalPreviewUri(localPhotoPath: string): Promise<string | null> {
    const path = localPhotoPath.trim();
    if (!path) {
      return null;
    }
    try {
      const { uri } = await Filesystem.getUri({ path, directory: Directory.Data });
      return Capacitor.convertFileSrc(uri);
    } catch {
      try {
        const result = await Filesystem.readFile({ path, directory: Directory.Data });
        const data = result.data;
        if (typeof data === 'string') {
          return data.startsWith('data:') ? data : `data:image/jpeg;base64,${data}`;
        }
      } catch {
        return null;
      }
    }
    return null;
  }

  private async writeLocalFile(workOrderId: number, checklistOrder: number, file: File): Promise<string> {
    const path = `checklist/${workOrderId}/${checklistOrder}.jpg`;
    const base64 = await this.fileToBase64(file);
    await Filesystem.writeFile({
      path,
      data: base64,
      directory: Directory.Data,
      recursive: true,
    });
    return path;
  }

  private async readLocalAsFile(localPath: string, checklistOrder: number, contentType: string): Promise<File> {
    const result = await Filesystem.readFile({ path: localPath, directory: Directory.Data });
    const data = result.data;
    let blob: Blob;
    if (typeof data === 'string') {
      blob = this.base64ToBlob(data, contentType);
    } else {
      blob = data as Blob;
    }
    return new File([blob], `checklist-${checklistOrder}.jpg`, { type: contentType });
  }

  private async uploadFileToBackend(
    workOrderId: number,
    checklistOrder: number,
    file: File,
    contentType: string,
  ): Promise<string> {
    const target = await this.api.requestChecklistPhotoUploadUrl(workOrderId, checklistOrder, contentType);

    if (target.uploadMethod === 'PUT' && target.uploadUrl && !Capacitor.isNativePlatform()) {
      try {
        const response = await fetch(target.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': target.contentType },
        });
        if (response.ok) {
          return target.imageUrl;
        }
      } catch {
        /* Browser CORS on the presigned URL — fall through to multipart. */
      }
    }

    return this.api.uploadChecklistPhotoForm(workOrderId, checklistOrder, file);
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read photo file'));
      reader.onload = () => {
        const result = String(reader.result ?? '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.readAsDataURL(file);
    });
  }

  private base64ToBlob(base64OrDataUrl: string, contentType: string): Blob {
    let base64 = base64OrDataUrl;
    if (base64.includes(',')) {
      base64 = base64.split(',')[1] ?? base64;
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: contentType });
  }
}
